// ============================================================================
// Client live-sync hook — opens the CLIENT-scoped /api/client/ws WebSocket and
// turns every `{type:'changed', entity, id, action}` event into a precise React
// Query cache invalidation, so a message (or read receipt) sent by the trainer
// updates the portal — thread + unread badge — with no refresh.
// ----------------------------------------------------------------------------
// The mirror of src/sync/useLiveSync (the trainer hook), but:
//   • connects /api/client/ws (authed via the vf_client_session cookie; the
//     server closes 1008 for an unauthenticated / non-active portal socket, and
//     we just back off and retry)
//   • registers under the client's id server-side; a trainer→client message is
//     delivered here as `{type:'changed', entity:'message', …}`
// Reuses the SAME ENTITY_INVALIDATIONS map as the trainer hook — the `message`
// entry includes the client message/unread roots, so this hook only needs to
// look up the map (no client-specific mapping to drift).
//
// Behaviour (identical posture to the trainer hook):
//   • only connects while authenticated (caller passes `enabled`)
//   • auto-reconnects with capped exponential backoff on unexpected close
//   • on a RECONNECT, sweeps all queries so anything missed during the offline
//     gap converges; cleans up on unmount / when disabled
// ============================================================================
import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ENTITY_INVALIDATIONS } from "../api";

/** The compact change event the backend broadcasts on every mutation. */
interface ChangedEvent {
  type: "changed";
  entity: string;
  id: number | null;
  action: "created" | "updated" | "deleted" | "read" | string;
}

function clientWsUrl(): string {
  const proto = window.location.protocol === "https:" ? "wss:" : "ws:";
  // Same-origin; Vite proxies /api/client/ws to the FastAPI backend in dev.
  return `${proto}//${window.location.host}/api/client/ws`;
}

export function useClientLiveSync(enabled: boolean): void {
  const qc = useQueryClient();
  // Keep the latest client without re-running the effect on every render.
  const qcRef = useRef(qc);
  qcRef.current = qc;

  useEffect(() => {
    if (!enabled) return;

    let socket: WebSocket | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let attempts = 0;
    let closed = false; // set on cleanup so we stop reconnecting
    let wasConnected = false; // true once a socket has opened at least once

    const connect = () => {
      if (closed) return;
      socket = new WebSocket(clientWsUrl());

      socket.onopen = () => {
        // On a RECONNECT, the WS only pushes changes while connected — anything
        // sent during the offline gap (sleep, network blip, backoff window) was
        // missed. Invalidate everything so the UI refetches and converges. The
        // first connect needs no sweep (queries load normally).
        if (wasConnected) {
          void qcRef.current.invalidateQueries();
        }
        wasConnected = true;
        attempts = 0;
      };

      socket.onmessage = (ev) => {
        let msg: unknown;
        try {
          msg = JSON.parse(ev.data as string);
        } catch {
          return;
        }
        if (
          !msg ||
          typeof msg !== "object" ||
          (msg as { type?: string }).type !== "changed"
        ) {
          return; // ignore the initial {type:'connected'} greeting + noise
        }
        const { entity } = msg as ChangedEvent;
        const roots = ENTITY_INVALIDATIONS[entity];
        if (!roots) return;
        for (const queryKey of roots) {
          void qcRef.current.invalidateQueries({ queryKey });
        }
      };

      socket.onclose = () => {
        if (closed) return;
        // Capped exponential backoff: 0.5s, 1s, 2s, 4s … max 15s.
        const delay = Math.min(15_000, 500 * 2 ** attempts);
        attempts += 1;
        reconnectTimer = setTimeout(connect, delay);
      };

      socket.onerror = () => {
        // Let onclose drive the reconnect; just close this socket.
        socket?.close();
      };
    };

    connect();

    return () => {
      closed = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (socket) {
        socket.onclose = null; // prevent reconnect on intentional close
        socket.close();
      }
    };
  }, [enabled]);
}
